import { createContext, useContext, useState, useEffect, ReactNode } from 'react'

interface GoogleUser {
  id: string;
  email: string;
  name: string;
  picture: string;
}

interface AuthContextType {
  user: GoogleUser | null;
  token: string | null;
  isLoading: boolean;
  signIn: () => void;
  signOut: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

// Google Client ID - loaded from environment variable
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || 'your-google-client-id';

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<GoogleUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Load Google Identity Services
    const loadGoogleScript = () => {
      if (document.getElementById('google-identity-script')) {
        initializeGoogleAuth();
        return;
      }

      const script = document.createElement('script');
      script.id = 'google-identity-script';
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = initializeGoogleAuth;
      document.head.appendChild(script);
    };

    const initializeGoogleAuth = () => {
      if (window.google) {
        // Initialize Google OAuth (no callback needed for OAuth2 flow)
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          auto_select: false,
          cancel_on_tap_outside: true,
        });

        // Check for existing JWT token
        const savedJwtToken = localStorage.getItem('auth_token');
        if (savedJwtToken) {
          // TODO: Verify JWT token with backend or decode to check expiry
          setToken(savedJwtToken);
          // For now, we'll validate on first API call
        }
      }
      setIsLoading(false);
    };

    loadGoogleScript();
  }, []);

  // Clean up sign-in button when user becomes authenticated
  useEffect(() => {
    if (user) {
      const existingButton = document.getElementById('google-signin-button');
      if (existingButton && existingButton.parentNode) {
        existingButton.parentNode.removeChild(existingButton);
      }
    }
  }, [user]);

  const signIn = () => {
    if (window.google) {
      // Use Google ID token flow to get proper JWT from backend
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: async (response: any) => {
          if (response.credential) {
            try {
              // Send Google ID token to backend to get JWT
              const loginResponse = await fetch(`${import.meta.env.VITE_API_BASE_URL}/auth/login`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  googleToken: response.credential
                })
              });

              const loginData = await loginResponse.json();
              
              if (loginData.success) {
                const { user, token } = loginData.data;
                
                setToken(token);
                setUser(user);
                localStorage.setItem('auth_token', token);
                localStorage.setItem('google_token', response.credential);
                console.log('User signed in:', user.name);
              } else {
                console.error('Backend login failed:', loginData.error);
                alert('Kirjautuminen epäonnistui: ' + loginData.error);
              }
            } catch (error) {
              console.error('Error during login:', error);
              const errorMessage = error instanceof Error ? error.message : 'Unknown error';
              alert('Kirjautuminen epäonnistui: ' + errorMessage);
            }
          }
        },
      });

      // Create callback function that will clean up the button
      const handleGoogleCallback = async (response: any) => {
        // First, remove the button immediately
        const existingButton = document.getElementById('google-signin-button');
        if (existingButton && existingButton.parentNode) {
          existingButton.parentNode.removeChild(existingButton);
        }

        if (response.credential) {
          try {
            // Send Google ID token to backend to get JWT
            const loginResponse = await fetch(`${import.meta.env.VITE_API_BASE_URL}/auth/login`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                googleToken: response.credential
              })
            });

            const loginData = await loginResponse.json();
            
            if (loginData.success) {
              const { user, token } = loginData.data;
              
              setToken(token);
              setUser(user);
              localStorage.setItem('auth_token', token);
              localStorage.setItem('google_token', response.credential);
              console.log('User signed in:', user.name);
            } else {
              console.error('Backend login failed:', loginData.error);
              alert('Kirjautuminen epäonnistui: ' + loginData.error);
            }
          } catch (error) {
            console.error('Error during login:', error);
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            alert('Kirjautuminen epäonnistui: ' + errorMessage);
          }
        }
      };

      // Initialize Google Sign-In with callback
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleCallback,
      });

      // Create and render sign-in button
      const buttonDiv = document.createElement('div');
      buttonDiv.id = 'google-signin-button';
      buttonDiv.style.position = 'fixed';
      buttonDiv.style.top = '50%';
      buttonDiv.style.left = '50%';
      buttonDiv.style.transform = 'translate(-50%, -50%)';
      buttonDiv.style.zIndex = '10000';
      buttonDiv.style.backgroundColor = 'white';
      buttonDiv.style.padding = '20px';
      buttonDiv.style.borderRadius = '8px';
      buttonDiv.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
      
      document.body.appendChild(buttonDiv);

      window.google.accounts.id.renderButton(buttonDiv, {
        theme: 'outline',
        size: 'large',
        text: 'signin_with',
        shape: 'rectangular',
      });
    } else {
      console.error('Google Identity Services not loaded');
      alert('Kirjautuminen epäonnistui. Kokeile päivittää sivu.');
    }
  };

  const signOut = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('google_token');
    localStorage.removeItem('auth_token');
    
    // Clean up any remaining sign-in buttons
    const existingButton = document.getElementById('google-signin-button');
    if (existingButton && existingButton.parentNode) {
      existingButton.parentNode.removeChild(existingButton);
    }
    
    if (window.google) {
      window.google.accounts.id.disableAutoSelect();
    }
    
    console.log('User signed out');
  };

  const value: AuthContextType = {
    user,
    token,
    isLoading,
    signIn,
    signOut,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Type declarations for Google Identity Services
declare global {
  interface Window {
    google: {
      accounts: {
        id: {
          initialize: (config: any) => void;
          prompt: () => void;
          disableAutoSelect: () => void;
          renderButton: (element: HTMLElement, config: any) => void;
        };
        oauth2: {
          initTokenClient: (config: any) => {
            requestAccessToken: () => void;
          };
        };
      };
    };
  }
} 